import { AspectRatio, Box, Grid, Image, Text, Icon } from "@chakra-ui/react";
import { InputDropzone } from "components/Atomos/Form/Elements/InputDropzone";
import { useEffect, useState } from "react";
import { RiCloseFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { uploadFotosAnimal } from "store/modules/zoonose/fotos/actions/uploadFotosAnimal";
import { getPreviewUploadFoto } from "utils/form-upload-foto";

interface ImageItem {
  file?: File;
  previewUrl: string;
  isExisting?: boolean;
  id?: string;
}

interface UploadFotoAnimalProps {
  idAnimal?: string;
  onFilesSelected?: (files: File[]) => void;
}

export const UploadFotoAnimal = ({
  idAnimal,
  onFilesSelected
}: UploadFotoAnimalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [images, setImages] = useState<ImageItem[]>([]);

  const { successPayload: detalheAnimal } = useTypedSelector(
    (state) => state.zoonose.obterDetalheAnimal
  );

  useEffect(() => {
    if (detalheAnimal?.arquivos && detalheAnimal.arquivos.length > 0) {
      const existingImages = detalheAnimal.arquivos.map((foto) => ({
        previewUrl: foto.fileUrl,
        isExisting: true,
        id: foto.id
      }));
      setImages(existingImages);
    }
  }, [detalheAnimal]);

  async function onDrop(droppedFiles: File[]) {
    const newImages = await Promise.all(
      droppedFiles.map(async (foto) => {
        const previewUrl = await getPreviewUploadFoto(foto);
        return { file: foto, previewUrl, isExisting: false };
      })
    );

    const updatedList = [
      ...images.filter((img) => img.isExisting),
      ...newImages
    ];
    setImages(updatedList);

    if (onFilesSelected) {
      const filesToSend = updatedList
        .filter((img) => !img.isExisting && img.file)
        .map((img) => img.file);

      onFilesSelected(filesToSend as File[]);
    }
  }

  function removeImage(indexToRemove: number) {
    setImages((prevImages) => {
      const newImages = prevImages.filter(
        (_, index) => index !== indexToRemove
      );

      if (onFilesSelected) {
        const filesToSend = newImages
          .filter((img) => !img.isExisting && img.file)
          .map((img) => img.file);

        onFilesSelected(filesToSend as File[]);
      }

      return newImages;
    });
  }

  const handleUpload = () => {
    if (idAnimal && images.length > 0) {
      const filesToUpload = images
        .filter((img) => !img.isExisting && img.file)
        .map((img) => img.file);

      if (filesToUpload.length > 0) {
        dispatch(
          uploadFotosAnimal({
            idAnimal: idAnimal,
            fotos: filesToUpload as File[],
            onSuccess: () => {
              setImages([]);
              navigate("/zoonose");
            }
          })
        );
      }
    }
  };

  useEffect(() => {
    if (idAnimal && images.length > 0) {
      handleUpload();
    }
  }, [idAnimal]);

  return (
    <Box>
      {images.length > 0 && (
        <Box>
          <Grid
            w="full"
            gridTemplateColumns={{
              base: "1fr 1fr",
              md: "1fr 1fr 1fr"
            }}
            gap={4}
          >
            {images.map((img, index) => (
              <Box key={img.id || img.previewUrl} position="relative">
                <AspectRatio
                  border="1px solid black"
                  borderColor="gray.200"
                  _dark={{ borderColor: "gray.600" }}
                >
                  <Box w="full">
                    <Image
                      src={img.previewUrl}
                      w="full"
                      h="full"
                      objectFit="contain"
                    />
                  </Box>
                </AspectRatio>
                <Box
                  w="1rem"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  h="1rem"
                  borderRadius="full"
                  bgColor="black"
                  position="absolute"
                  top="-2"
                  right="-2"
                  cursor="pointer"
                  _hover={{ bgColor: "gray.600" }}
                  onClick={() => removeImage(index)}
                >
                  <Icon as={RiCloseFill} color="white" />
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      <Box mt={3} h="5rem">
        <Text fontWeight="medium" fontSize="sm" mb={1}>
          Upload de fotos
        </Text>
        <InputDropzone
          message="Clique ou arraste a sua imagem"
          subMessage="Max: 1mb / JPG e PNG"
          onDrop={onDrop}
          accept={["image/png", "image/jpeg"]}
          multiple={true}
          maxFiles={5}
        />
      </Box>
    </Box>
  );
};
